'use client'

import { useCallback } from 'react'
import type { AnalyticsEventInput } from '../lib/product-analytics'
import { sanitizeAnalyticsPath, sanitizeAnalyticsReferrer } from '../lib/analytics-sanitize'

function ensureAnonymousId() {
  try {
    const key = 'masjids_analytics_anonymous_id'
    const existing = localStorage.getItem(key)
    if (existing) return existing
    const next = `anon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
    localStorage.setItem(key, next)
    return next
  } catch {
    return `anon_${Math.random().toString(36).slice(2, 10)}`
  }
}

function readUser() {
  try {
    const userId = localStorage.getItem('user_id') || undefined
    const role = localStorage.getItem('user_role') || undefined
    return { userId, role }
  } catch {
    return {}
  }
}

export function useProductAnalytics() {
  return useCallback((input: AnalyticsEventInput) => {
    if (typeof window === 'undefined') return
    const user = readUser()
    const payload: AnalyticsEventInput = {
      anonymousId: ensureAnonymousId(),
      sessionId: sessionStorage.getItem('masjids_analytics_session_id') || undefined,
      path: window.location.pathname,
      referrer: document.referrer || undefined,
      ...input,
      userId: input.userId || user.userId,
      role: input.role || user.role as AnalyticsEventInput['role'],
    }

    /* Sanitize AFTER the spread — `...input` can override path, and document.referrer is the
       FULL previous URL, so leaving /reset-password?token=… in either field would persist a
       live reset token. Applied here (not just in the tracker) because six other components
       call this hook directly, and referrer was leaking for all of them. Allowlist-only. */
    payload.path = sanitizeAnalyticsPath(payload.path) ?? undefined
    payload.referrer = sanitizeAnalyticsReferrer(payload.referrer) ?? undefined

    try {
      if (!payload.sessionId) {
        payload.sessionId = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
        sessionStorage.setItem('masjids_analytics_session_id', payload.sessionId)
      }
    } catch {}

    const body = JSON.stringify(payload)
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon('/api/analytics/events', blob)
      return
    }

    fetch('/api/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {})
  }, [])
}
